import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getRecentlyViewed, clearRecentlyViewed } from '../utils/recentlyViewed';
import { formatMoney } from '../utils/formatters';
import { getAdTitle } from '../utils/adI18n';
import { guardImage, getCategoryPlaceholder } from '../lib/imageTrust';
import './RecentlyViewedCarousel.css';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

/**
 * RecentlyViewedCarousel — per-user horizontal strip.
 *
 *   ✓ Per-user scoping (via getRecentlyViewed(userId)).
 *   ✓ No duplicates, max 20 (enforced in /utils/recentlyViewed.js).
 *   ✓ Clear-all button with inline confirmation.
 *   ✓ Hides itself entirely when empty — returns null. No blank space.
 *   ✓ Reserves no vertical space until data is ready, so it cannot cause
 *     layout shift on Home / Shop above-the-fold.
 *   ✓ Fetches full ad docs from /api/ads/by-ids so the strip works even
 *     when the visited ad is not in the current page's filtered list.
 *   ✓ RTL / LTR safe — `dir` is inherited from the page; the scroller
 *     uses logical properties.
 *
 * Props:
 *   max   – cap (default 10; user spec: 10–20).
 *   limit – visible cards in first row (default same as max).
 */
export default function RecentlyViewedCarousel({ max = 10 }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isArabic = i18n.language?.startsWith('ar');
  const lang = isArabic ? 'ar' : 'en';

  const userKey = user?.user_id || user?.id || '';
  const hasUser = typeof userKey === 'string' && userKey.trim().length > 0;

  // Read IDs from localStorage. Resync when userKey or location changes.
  // When there's no logged-in user, the util returns [] — no unscoped
  // fallback — so anonymous/pre-auth sessions can never leak data.
  const [ids, setIds] = useState(() => (hasUser ? getRecentlyViewed(userKey).slice(0, max) : []));
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);

  // Re-read on mount + when user changes, and when the tab regains focus
  // (so visiting an ad in another tab reflects on return). On user switch
  // we also wipe the in-memory items immediately so the previous user's
  // cards can't flash on screen before the new fetch resolves.
  useEffect(() => {
    if (!hasUser) {
      setIds([]);
      setItems([]);
      setLoaded(true);
      return undefined;
    }
    setItems([]);
    setLoaded(false);
    setIds(getRecentlyViewed(userKey).slice(0, max));
    const onFocus = () => setIds(getRecentlyViewed(userKey).slice(0, max));
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [userKey, hasUser, max]);

  useEffect(() => {
    if (!ids || ids.length === 0) {
      setItems([]);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/ads/by-ids`, { ad_ids: ids });
        if (cancelled) return;
        setItems(res?.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [ids]);

  const handleClear = useCallback(() => {
    if (!confirmingClear) {
      setConfirmingClear(true);
      // Auto-reset confirmation after 4s so the UI never gets stuck.
      setTimeout(() => setConfirmingClear(false), 4000);
      return;
    }
    clearRecentlyViewed(userKey);
    setIds([]);
    setItems([]);
    setConfirmingClear(false);
  }, [confirmingClear, userKey]);

  // Hide entirely for anonymous / pre-auth sessions — prevents cross-user
  // data leakage from any lingering unscoped bucket.
  if (!hasUser) return null;

  // Hide entirely when there is nothing to show — prevents blank space.
  if (loaded && items.length === 0) return null;
  if (!loaded && ids.length === 0) return null;

  // Loading guard — don't reserve space; render nothing until we have data.
  if (!loaded) return null;

  const getImageUrl = (ad) => {
    const images = ad.images || [];
    if (images.length === 0) return getCategoryPlaceholder(ad.category_id || ad.category);
    return guardImage(images[0], ad.category_id || ad.category);
  };

  return (
    <section
      className="rv-section"
      data-testid="recently-viewed"
      aria-label={t('recently.recentlyViewed', { defaultValue: 'Recently Viewed' })}
    >
      <header className="rv-header">
        <h3 className="rv-title" data-testid="recently-viewed-title">
          {t('recently.recentlyViewed', { defaultValue: 'Recently Viewed' })}
        </h3>
        <button
          type="button"
          className={`rv-clear-btn ${confirmingClear ? 'rv-clear-btn--confirm' : ''}`}
          onClick={handleClear}
          data-testid="recently-viewed-clear-btn"
        >
          <Trash2 size={14} aria-hidden="true" />
          <span>
            {confirmingClear
              ? t('recently.confirmClear', { defaultValue: 'Tap again to clear' })
              : t('recently.clear', { defaultValue: 'Clear' })}
          </span>
        </button>
      </header>

      <div className="rv-scroll" role="list">
        {items.map((ad) => (
          <button
            key={`rv-${ad.ad_id}`}
            type="button"
            role="listitem"
            className="rv-card"
            onClick={() => navigate(`/ad/${ad.ad_id}`)}
            data-testid={`recently-viewed-card-${ad.ad_id}`}
          >
            <div className="rv-thumb">
              {ad.images?.[0] ? (
                <img src={getImageUrl(ad)} alt="" loading="lazy" />
              ) : (
                <span className="rv-thumb-fallback" aria-hidden="true">📦</span>
              )}
            </div>
            <div className="rv-info">
              <span className="rv-name">
                {(getAdTitle(ad, lang).text || ad.title || '').slice(0, 28)}
              </span>
              <span className="rv-price">
                {formatMoney(ad.price, {
                  locale: isArabic ? 'ar-SD' : 'en',
                  currency: ad.currency || 'SDG',
                })}
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
